"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _Animated = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Animated"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _StackView = _interopRequireDefault(require("../StackView"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _useSkeletonNativeAnimation = _interopRequireDefault(require("./useSkeletonNativeAnimation"));
var _skeletonWebAnimation = _interopRequireDefault(require("./skeletonWebAnimation"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);
const selectSkeletonStyles = _ref => {
  let {
    color,
    radius
  } = _ref;
  return {
    backgroundColor: color,
    borderRadius: radius,
    maxWidth: '100%'
  };
};
const selectLineStyles = _ref2 => {
  let {
    skeletonHeight,
    lineWidth,
    radius
  } = _ref2;
  return {
    width: lineWidth,
    height: skeletonHeight,
    borderRadius: radius
  };
};
const selectShapeStyles = _ref3 => {
  let {
    skeletonHeight
  } = _ref3;
  return {
    height: skeletonHeight,
    width: skeletonHeight
  };
};
const selectSquareStyles = _ref4 => {
  let {
    radius
  } = _ref4;
  return {
    borderRadius: radius
  };
};
const Skeleton = /*#__PURE__*/_react.default.forwardRef((_ref5, ref) => {
  let {
    tokens,
    variant,
    size,
    sizeIndex = size,
    sizePixels,
    characters,
    lines,
    shape = 'line',
    ...rest
  } = _ref5;
  const themeTokens = (0, _ThemeProvider.useThemeTokens)('Skeleton', tokens, variant);
  const pixels = (0, _utils.useResponsiveProp)(sizePixels);
  const spacingScaleValue = typeof pixels === 'number' ?
  // Size by an exact number of pixels
  {
    options: {
      size: pixels
    }
  } :
  // Size by an index on the spacing scale (getting default index from theme if none provided)
  sizeIndex || themeTokens.size;
  const skeletonHeight = (0, _utils.useSpacingScale)(spacingScaleValue);

  // Animation color and animation duration
  const {
    secondColor,
    animationDuration
  } = themeTokens;
  const nativeAnimation = (0, _useSkeletonNativeAnimation.default)({
    animationDuration
  });
  const getAnimationBasedOnPlatform = () => {
    const animation = _Platform.default.OS === 'web' ? (0, _skeletonWebAnimation.default)(secondColor, animationDuration) : nativeAnimation;
    // We must pass the animation styles through `StyleSheet.create`
    // @see https://github.com/necolas/react-native-web/issues/2387
    const styles = _StyleSheet.default.create({
      animation
    });
    return styles.animation;
  };
  const getLineWidth = () => {
    if (characters) {
      return characters * themeTokens.baseWidth;
    }
    return themeTokens.characters * themeTokens.baseWidth;
  };
  const getStyledBasedOnShape = () => {
    if (shape === 'circle') {
      return selectShapeStyles({
        skeletonHeight
      });
    }
    if (shape === 'box') {
      return [selectShapeStyles({
        skeletonHeight
      }), selectSquareStyles({
        radius: themeTokens.squareRadius
      })];
    }
    return selectLineStyles({
      skeletonHeight,
      lineWidth: getLineWidth(),
      radius: themeTokens.lineRadius
    });
  };
  const renderSkeleton = function () {
    let index = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    // @see https://github.com/necolas/react-native-web/issues/2387
    const Component = _Platform.default.OS === 'web' ? _View.default : _Animated.default.View;
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(Component, {
      testID: "skeleton",
      style: [selectSkeletonStyles(themeTokens), getAnimationBasedOnPlatform(), getStyledBasedOnShape()]
    }, `skeleton-${index + 1}`);
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_StackView.default, {
    space: themeTokens.spaceBetweenLines,
    ref: ref,
    ...selectProps(rest),
    children: lines > 1 ? [...Array(lines)].map((_, index) => renderSkeleton(index)) : renderSkeleton()
  });
});
Skeleton.displayName = 'Skeleton';
Skeleton.propTypes = {
  ...selectedSystemPropTypes,
  tokens: (0, _utils.getTokensPropType)('Skeleton'),
  variant: _utils.variantProp.propType,
  /**
   * Sets the size of Skeleton lines or shape according to the theme's spacing scale. For example, size={1} gives the smallest non-zero theme-defined spacing size.
   *
   * May also accept an object with responsive viewport keys or spacing scale options - see `useSpacingScale` for details.
   */
  sizeIndex: _utils.spacingProps.types.spacingValue,
  /**
   * @deprecated alias for `sizeIndex`
   */
  size: _utils.spacingProps.types.spacingValue,
  /**
   * Sets the size of Skeleton lines or shape to an exact number of pixels. Use when it's necessary to exactly match sizes of images or other boxes.
   *
   * Accepts a number or an object with responsive viewport keys, e.g. { xs: 32, lg: 64 } would be 32px at xs, sm and md and 64 at lg and xl viewports.
   */
  sizePixels: _utils.responsiveProps.getTypeOptionallyByViewport(_propTypes.default.number),
  /**
   * Determines the width of simulated lines of text if the Skeleton's shape is 'line' (the default shape).
   *
   * Only has any affect if shape is line (the default). If unset, takes a default value from the theme.
   */
  characters: _propTypes.default.number,
  /**
   * Determines how many Skeleton items are rendered (default 1).
   *
   * Recommended usage is to simulate paragraphs of text when Skeleton's shape is 'line' (the default shape).
   *
   * The amount of spacing between multiple lines is controlled by theme tokens.
   */
  lines: _propTypes.default.number,
  /**
   * Determines if the skeleton should resemble lines of text (default), a circle, or a square box with themed rounded corners.
   */
  shape: _propTypes.default.oneOf(['line', 'circle', 'box'])
};
var _default = exports.default = Skeleton;